import React from "react";
import * as yup from "yup";
import { FormikHelpers, useFormik } from "formik";

import {
  JudgmentIntegrationImportCommand,
  ImportFormProps,
  CSFile,
  SaveJudgmentIntegrationCommand,
  JudgmentIntegration,
  IntegrationCategory,
} from "../types/JudgmentIntegrationTypes";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { Message } from "primereact/message";
import JudgmentClient from "../Api/judgment-client";
import judgmentStore from "../stores/judgment-store";

export const ImportForm: React.FC<ImportFormProps> = function (props) {
  const validateFile = (file: any): boolean => {
    if (
      file.size === 0 ||
      file.size === undefined ||
      file === undefined ||
      file === null
    ) {
      return false;
    }
    return true;
  };

  const headerTemplate = () => {
    if (
      judgmentStore.currentIntegration?.integrationCategoryID ===
      IntegrationCategory.Judgment
    ) {
      return judgmentStore.isEditing
        ? "Edit Imported Judgment Details"
        : "Import New Judgment Details";
    } else {
      return judgmentStore.isEditing
        ? "Edit Imported Satisfaction Details"
        : "Import New Satisfaction Details";
    }
  };

  //File Handlers
  const createFileObject = (
    fileName: string,
    fileSize: number,
    fileBytes: string
  ): CSFile => {
    return {
      fileName: fileName,
      fileContents: fileBytes,
      fileSizeMB: fileSize / 1024 / 1024,
    };
  };

  const judgmentFileUploadHandler = (event: any) => {
    let file = event.target.files[0];
    if (!validateFile(file)) {
      formikProps.setFieldError(
        "judgmentsFile",
        "The Civil Entries file is empty or not a valid file."
      );
      return;
    }

    let fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = () => {
      let upload = createFileObject(file.name, file.size, fr.result as string);
      if (fr.error) {
        props.showToast(
          "error",
          "There was an error importing the selected file. Please check the file contents.",
          fr.error?.message,
          undefined,
          true
        );
      } else {
        formikProps.setFieldValue("judgmentsFile", upload);
      }
    };
  };

  const aliasFileUploadHandler = (event: any) => {
    let file = event.target.files[0];

    if (!validateFile(file)) {
      formikProps.setFieldError(
        "aliasesFile",
        "The Aliases file is empty or not a valid file."
      );
      return;
    }

    let fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = () => {
      let upload = createFileObject(file.name, file.size, fr.result as string);
      if (fr.error) {
        props.showToast(
          "error",
          "There was an error importing the selected file. Please check the file contents.",
          fr.error?.message,
          undefined,
          true
        );
      } else {
        formikProps.setFieldValue("aliasesFile", upload);
      }
    };
  };

  const formikProps = useFormik<JudgmentIntegrationImportCommand>({
    initialValues: {
      judgmentIntegrationName: judgmentStore.currentIntegration
        ? judgmentStore.currentIntegration.name
        : "",
      judgmentsFile: undefined,
      aliasesFile: undefined,
      integrationCategoryID:
        judgmentStore.currentIntegration?.integrationCategoryID,
    },
    validationSchema: yup.object().shape({
      judgmentIntegrationName: yup
        .string()
        .required("Integration name is required."),
      judgmentsFile: judgmentStore.isEditing
        ? yup.object().notRequired()
        : yup.object().required("Civil Entries file is required."),
    }),
    onSubmit: async (
      values: JudgmentIntegrationImportCommand,
      { setSubmitting }: FormikHelpers<JudgmentIntegrationImportCommand>
    ) => {
      let response = await JudgmentClient.importJudgments(values);
      if (response.errors && response.errors.length > 0) {
        judgmentStore.currentIntegration = undefined;
        props.showToast(
          "error",
          response.message ?? "",
          response.errors[0],
          undefined,
          true
        );
      } else {
        judgmentStore.currentIntegration = response.judgmentIntegration;
        props.showToast("success", response.message ?? "", undefined, 5000);
        judgmentStore.showImportResultsTable = true;
        judgmentStore.setEditing(response.judgmentIntegration);
      }

      setSubmitting(false);
    },

    validateOnBlur: false,
    validateOnChange: false,
    enableReinitialize: true,
  });
  const nameHeaderFile = judgmentStore.currentIntegration?.integrationCategoryID === IntegrationCategory.Judgment ? "Judgment Integration Name:" : "Satisfaction Integration Name";
  const judgmentfileType = judgmentStore.currentIntegration?.integrationCategoryID === IntegrationCategory.Judgment ? "Civil Entries File" : "Civil Satisfaction File";
  return (
    <>
      <Panel header={headerTemplate()}>
        <form onSubmit={formikProps.handleSubmit}>

          <div className="p-fluid">
            <div className="p-field p-grid">
              <label htmlFor="judgmentIntegrationName" className="p-col-3 p-md-3 " >
                {nameHeaderFile}
              </label>
              <div className="p-col-9 p-md-9">
                <InputText
                  name="judgmentIntegrationName"
                  className={
                    formikProps.errors.judgmentIntegrationName &&
                      formikProps.touched.judgmentIntegrationName
                      ? "p-invalid"
                      : ""
                  }
                  value={formikProps.values.judgmentIntegrationName}
                  onChange={formikProps.handleChange}
                />
                {formikProps.errors.judgmentIntegrationName &&
                  formikProps.touched.judgmentIntegrationName && (
                    <Message
                      severity="error"
                      text={formikProps.errors.judgmentIntegrationName}
                    ></Message>
                  )}
              </div>
            </div>

            <div className="p-field p-grid">
              <div className="p-col-3 p-md-3">
                <label>{judgmentfileType}:</label>
                &nbsp;&nbsp;
              </div>
              <div className="p-col-9 p-md-9">
                <input
                  name="judgmentsFile"
                  type="file"
                  accept=".txt"
                  onChange={judgmentFileUploadHandler}
                />
                {formikProps.errors.judgmentsFile &&
                  formikProps.touched.judgmentsFile && (
                    <Message
                      severity="error"
                      content={formikProps.errors.judgmentsFile}
                    ></Message>
                  )}
              </div>



            </div>

            {judgmentStore.currentIntegration?.integrationCategoryID ===
              IntegrationCategory.Judgment ? (
              <div className="p-field p-grid">
                <div className="p-col-3 p-md-3">
                  <label>Alias File:</label>
                  &nbsp;&nbsp;
                </div>
                <div className="p-col-9 p-md-9">
                  <input
                    name="aliasesFile"
                    type="file"
                    onChange={aliasFileUploadHandler}
                    accept=".txt"
                  />
                  {formikProps.errors.aliasesFile &&
                    formikProps.touched.aliasesFile && (
                      <Message
                        severity="error"
                        content={formikProps.errors.aliasesFile}
                      ></Message>
                    )}
                </div>
              </div>
            ) : null}
            <div className="p-field p-col-2">
              <Button
                name="submit"
                type="submit"
                label={judgmentStore.isEditing ? "Save" : "Import"}
                disabled={formikProps.isSubmitting}
              />
            </div>
          </div>
        </form>
      </Panel>
    </>
  );
};
